import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Empty, Space, Switch, Table, Tag, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import {
    OrganizationPaymentMethod,
    PaymentMethodCatalogue
} from '../store/user/types';
import {
    getSalesDocumentOrganizationPaymentMethodsAPI,
    getSalesDocumentPaymentMethodCatalogueAPI,
    postSalesDocumentOrganizationPaymentMethodAPI,
    putSalesDocumentOrganizationPaymentMethodAPI
} from '../store/user/restCallUser';

const { Text } = Typography;

interface PaymentMethodsManagementProps {
    organizationId: string;
    organizationName?: string;
    onChanged?: () => Promise<void> | void;
}

interface PaymentMethodRow extends PaymentMethodCatalogue {
    organizationMethod?: OrganizationPaymentMethod;
}

function responseData(response: any): any {
    return response?.Response?.data ?? response?.Response ?? response?.data ?? [];
}

function responseError(response: any): string | null {
    if (!response || response.Error !== null || response.StatusCode < 200 || response.StatusCode >= 300) {
        return response?.Error?.message || response?.Error || 'No se pudo completar la operación.';
    }
    return null;
}

function PaymentMethodsManagement({
    organizationId,
    organizationName,
    onChanged
}: PaymentMethodsManagementProps) {
    const [catalogue, setCatalogue] = useState<PaymentMethodCatalogue[]>([]);
    const [organizationMethods, setOrganizationMethods] = useState<OrganizationPaymentMethod[]>([]);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!organizationId) return;
        setLoading(true);
        setError(null);
        try {
            const [catalogueResponse, organizationResponse] = await Promise.all([
                getSalesDocumentPaymentMethodCatalogueAPI(),
                getSalesDocumentOrganizationPaymentMethodsAPI(organizationId)
            ]);
            const catalogueError = responseError(catalogueResponse);
            const organizationError = responseError(organizationResponse);
            if (catalogueError || organizationError) {
                throw new Error(catalogueError || organizationError || 'No se pudo cargar el catálogo.');
            }
            setCatalogue(responseData(catalogueResponse) as PaymentMethodCatalogue[]);
            setOrganizationMethods(responseData(organizationResponse) as OrganizationPaymentMethod[]);
        } catch (loadError: any) {
            setError(loadError?.message || 'No se pudo cargar la configuración de métodos de pago.');
        } finally {
            setLoading(false);
        }
    }, [organizationId]);

    useEffect(() => {
        void load();
    }, [load]);

    const rows = useMemo<PaymentMethodRow[]>(() => catalogue.map(item => ({
        ...item,
        organizationMethod: organizationMethods.find(
            organizationMethod => organizationMethod.paymentMethodCatalogueId === item.id
        )
    })), [catalogue, organizationMethods]);

    const saveMethod = async (
        row: PaymentMethodRow,
        changes: { enabled?: boolean; isDefault?: boolean }
    ) => {
        const current = row.organizationMethod;
        const enabled = changes.enabled ?? current?.status === 'ACTIVO';
        const isDefault = enabled && (changes.isDefault ?? current?.isDefault ?? false);
        setSavingId(row.id);
        setError(null);
        try {
            const payload = {
                paymentMethodCatalogueId: row.id,
                isDefault,
                displayOrder: current?.displayOrder || 0,
                status: enabled ? 'ACTIVO' : 'INACTIVO'
            };
            const response = current
                ? await putSalesDocumentOrganizationPaymentMethodAPI(organizationId, current.id, payload)
                : await postSalesDocumentOrganizationPaymentMethodAPI(organizationId, payload);
            const operationError = responseError(response);
            if (operationError) throw new Error(operationError);
            message.success(enabled
                ? `${row.name} habilitado para la organización.`
                : `${row.name} deshabilitado para la organización.`);
            await load();
            await onChanged?.();
        } catch (saveError: any) {
            setError(saveError?.message || 'No se pudo actualizar el método de pago.');
        } finally {
            setSavingId(null);
        }
    };

    return (
        <Card
            title="Métodos de pago"
            extra={<Button type="link" icon={<ReloadOutlined />} onClick={() => void load()}>Actualizar</Button>}
            style={{ marginBottom: 16 }}
        >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Alert
                    type="info"
                    showIcon
                    message="Configuración de pagos para Blue"
                    description={`Habilita los medios de pago que ${organizationName || 'la organización'} podrá utilizar. Solo puede existir un método predeterminado activo.`}
                />
                {error && <Alert type="error" showIcon message={error} />}
                {!loading && rows.length === 0 ? (
                    <Empty description="No hay métodos de pago activos en el catálogo global." />
                ) : (
                    <Table<PaymentMethodRow>
                        rowKey="id"
                        loading={loading}
                        pagination={false}
                        dataSource={rows}
                        columns={[
                            { title: 'Código', dataIndex: 'code', width: 150 },
                            { title: 'Método', dataIndex: 'name' },
                            { title: 'Categoría', dataIndex: 'category', width: 180 },
                            { title: 'Código SRI', dataIndex: 'sriCode', width: 120 },
                            {
                                title: 'Habilitado',
                                width: 130,
                                render: (_, row) => (
                                    <Switch
                                        checked={row.organizationMethod?.status === 'ACTIVO'}
                                        loading={savingId === row.id}
                                        onChange={checked => void saveMethod(row, { enabled: checked })}
                                    />
                                )
                            },
                            {
                                title: 'Predeterminado',
                                width: 160,
                                render: (_, row) => (
                                    <Space>
                                        <Switch
                                            checked={row.organizationMethod?.isDefault === true}
                                            disabled={row.organizationMethod?.status !== 'ACTIVO'}
                                            loading={savingId === row.id}
                                            onChange={checked => void saveMethod(row, { isDefault: checked })}
                                        />
                                        {row.organizationMethod?.isDefault && <Tag color="blue">Principal</Tag>}
                                    </Space>
                                )
                            },
                            {
                                title: 'Referencia',
                                width: 140,
                                render: (_, row) => row.requiresReference ? <Tag>Requerida</Tag> : <Text type="secondary">No requerida</Text>
                            }
                        ]}
                    />
                )}
            </Space>
        </Card>
    );
}

export default PaymentMethodsManagement;
